import * as React from 'react';
import Textarea from 'react-textarea-autosize';
import debounce from 'lodash/debounce';
import styles from './textarea.scss';

export interface DebounceTextareaProps {
  id?: string;
  type?: string;
  className?: string;
  style?: any;
  value: string;
  placeholder: string;
  readOnly?: boolean;
  maxLength?: number;
  focus?: boolean;
  onChange: (value: string, openOptionsView?: boolean) => void;
  onFocus?: (e: React.FormEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FormEvent<HTMLTextAreaElement>) => void;

  rows?: number;
  autoExpandable?: boolean;
  wait?: number;
}

export interface DebounceTextareaState {
  height?: number;
  value?: string;
  hasFocus?: boolean;
}

export class DebounceTextarea extends React.Component<DebounceTextareaProps, DebounceTextareaState> {
  public static defaultProps: Partial<DebounceTextareaProps> = {
    wait: 500,
    rows: 1,
    autoExpandable: true
  };

  public static getDerivedStateFromProps(props:DebounceTextareaProps, state: DebounceTextareaState) {
    // The user might be typing, only set value from server if we're blurred.
    if (!state.hasFocus && props.value !== state.value) {
      return {
        value: props.value
      };
    }

    return null;
  }

  private debounceOnChange: any;

  constructor(props: DebounceTextareaProps) {
    super(props);
    this.state = {
      value: this.props.value,
      hasFocus: false,
      height: 0
    } as DebounceTextareaState;

    this.debounceOnChange = debounce((value: string) => {
      this.props.onChange(value);
    }, this.props.wait);
  }

  private onChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const currentTarget = e.currentTarget;
    this.setState({
      value: currentTarget.value
    } as DebounceTextareaState, () => {
      this.debounceOnChange(currentTarget.value);
    });
  };

  private onFocus = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const eventClone = Object.assign({}, e);
    this.setState({ hasFocus: true }, () => {
      this.props.onFocus && this.props.onFocus(eventClone);
    });
  };

  private onBlur = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const eventClone = Object.assign({}, e);
    if (this.props.value !== this.state.value) {
      this.props.onChange && this.props.onChange(this.state.value, false);
    }
    this.setState({ hasFocus: false }, () => {
      this.props.onBlur && this.props.onBlur(eventClone);
    });
  };

  public render() {
    const {
      autoExpandable,
      maxLength,
      readOnly,
      placeholder,
      rows,
      focus
    } = this.props;

    const { readOnlyStyle, textarea } = styles;

    const maxRows = !autoExpandable ? 1 : undefined;
    return (<div className={`form-control ${readOnly ? readOnlyStyle : ''}
    ${this.state.hasFocus ? 'form-control-focus' : ''}`}>
      <Textarea
        autoFocus={focus}
        className={textarea}
        value={this.state.value}
        readOnly={readOnly}
        disabled={readOnly}
        maxLength={maxLength}
        placeholder={!readOnly ? placeholder : undefined}
        onChange={this.onChange}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        minRows={readOnly ? 1 : rows}
        maxRows={maxRows}
      />
    </div>);
  }
}
